/**
 * NotificationProvider — React context for in-app notification polling and state.
 *
 * Features:
 * - 30-second polling for unread count
 * - Visibility API pause/resume
 * - Exponential backoff on network errors
 * - New notification detection for toast triggers
 * - Channel preferences management
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { NotificationRecord, NotificationPreferences } from '@/types/notifications';
import {
  fetchUnreadCount,
  fetchNotifications,
  markNotificationRead,
  markAllRead as apiMarkAllRead,
  deleteNotification as apiDeleteNotification,
  fetchPreferences,
  updatePreferences as apiUpdatePreferences,
  AuthError,
} from '@/services/notificationApi';
import { useAuth } from '@/hooks/useAuth';

const POLL_INTERVAL_MS = 30_000;
const MAX_BACKOFF_MS = 300_000;

interface NotificationContextValue {
  unreadCount: number | null;
  notifications: NotificationRecord[];
  newNotifications: NotificationRecord[];
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  preferences: NotificationPreferences | null;
  updatePreferences: (prefs: Partial<NotificationPreferences>) => Promise<void>;
  clearNewNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [newNotifications, setNewNotifications] = useState<NotificationRecord[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);

  const prevCountRef = useRef<number | null>(null);
  const backoffRef = useRef(POLL_INTERVAL_MS);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  // Load preferences on mount
  useEffect(() => {
    if (!user) return;
    fetchPreferences()
      .then(setPreferences)
      .catch(() => {});
  }, [user]);

  const poll = useCallback(async () => {
    if (!user) return;

    try {
      const count = await fetchUnreadCount();
      if (!isMountedRef.current) return;

      // Detect new notifications
      if (prevCountRef.current !== null && count > prevCountRef.current) {
        try {
          const unread = await fetchNotifications('unread');
          if (isMountedRef.current) {
            // Find truly new ones (not already in notifications state)
            const existingIds = new Set(notifications.map((n) => n.notification_id));
            const fresh = unread.filter((n) => !existingIds.has(n.notification_id));
            if (fresh.length > 0) {
              setNewNotifications((prev) => [...prev, ...fresh]);
            }
            setNotifications(unread);
          }
        } catch {
          // Non-critical — count update still applies
        }
      }

      setUnreadCount(count);
      prevCountRef.current = count;
      backoffRef.current = POLL_INTERVAL_MS;
    } catch (error) {
      if (error instanceof AuthError) {
        // Try to refresh auth, if that fails redirect to login
        try {
          const count = await fetchUnreadCount();
          if (isMountedRef.current) {
            setUnreadCount(count);
            prevCountRef.current = count;
            backoffRef.current = POLL_INTERVAL_MS;
          }
          return;
        } catch {
          // Double 401 — stop polling, sign out
          signOut();
          return;
        }
      }

      // Network error — exponential backoff
      backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
    }
  }, [user, notifications, signOut]);

  // Polling loop
  useEffect(() => {
    if (!user) return;

    isMountedRef.current = true;

    const schedulePoll = () => {
      timerRef.current = setTimeout(async () => {
        await poll();
        if (isMountedRef.current) {
          schedulePoll();
        }
      }, backoffRef.current);
    };

    // Initial poll
    poll().then(() => {
      if (isMountedRef.current) {
        schedulePoll();
      }
    });

    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [user, poll]);

  // Visibility API: pause/resume
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Immediately poll and restart interval
        backoffRef.current = POLL_INTERVAL_MS;
        poll();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [poll]);

  const markRead = useCallback(async (id: string) => {
    await markNotificationRead(id);
    setNotifications((prev) =>
      prev.map((n) => (n.notification_id === id ? { ...n, is_read: true } : n))
    );
    setUnreadCount((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
  }, []);

  const markAllReadHandler = useCallback(async () => {
    await apiMarkAllRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }, []);

  const deleteNotificationHandler = useCallback(async (id: string) => {
    await apiDeleteNotification(id);
    setNotifications((prev) => prev.filter((n) => n.notification_id !== id));
    // Decrease count if the deleted notification was unread
    setUnreadCount((prev) => {
      const wasUnread = notifications.find(
        (n) => n.notification_id === id && !n.is_read
      );
      return prev !== null && wasUnread ? prev - 1 : prev;
    });
  }, [notifications]);

  const refreshNotifications = useCallback(async () => {
    try {
      const [count, items] = await Promise.all([
        fetchUnreadCount(),
        fetchNotifications(),
      ]);
      setUnreadCount(count);
      setNotifications(items);
      prevCountRef.current = count;
    } catch {
      // Silent fail on refresh
    }
  }, []);

  const updatePreferencesHandler = useCallback(
    async (prefs: Partial<NotificationPreferences>) => {
      const updated = await apiUpdatePreferences(prefs);
      setPreferences(updated);
    },
    []
  );

  const clearNewNotifications = useCallback(() => {
    setNewNotifications([]);
  }, []);

  const value: NotificationContextValue = {
    unreadCount,
    notifications,
    newNotifications,
    markRead,
    markAllRead: markAllReadHandler,
    deleteNotification: deleteNotificationHandler,
    refreshNotifications,
    preferences,
    updatePreferences: updatePreferencesHandler,
    clearNewNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook to access notification context.
 */
export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
